/**
 * AI Recruit - Job Info Tooltip
 * Hover tooltip for the Job Postings table's created/updated info icon.
 * Positions itself with a portaled, viewport-clamped fixed box instead of
 * plain CSS absolute positioning, so it doesn't spill into neighboring table
 * columns/rows the way a `max-w-xs`, centered-under-the-icon tooltip does
 * once rows are this compact.
 */

import React, { useState, useRef } from 'react'
import { createPortal } from 'react-dom'

interface JobInfoTooltipProps {
  content: string
  children: React.ReactElement
}

const TOOLTIP_WIDTH = 260
const MARGIN = 8

const JobInfoTooltip: React.FC<JobInfoTooltipProps> = ({ content, children }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLSpanElement>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const computePosition = () => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const estimatedHeight = 96
    const openUpward = rect.bottom + estimatedHeight + MARGIN > window.innerHeight

    let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2
    left = Math.min(Math.max(left, MARGIN), window.innerWidth - TOOLTIP_WIDTH - MARGIN)

    const top = openUpward ? rect.top - estimatedHeight - 8 : rect.bottom + 8

    setPosition({ top, left })
  }

  const show = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    computePosition()
    setIsVisible(true)
  }

  const hide = () => {
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), 50)
  }

  return (
    <span ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} className="inline-flex">
      {children}
      {isVisible &&
        createPortal(
          <div
            className="fixed z-50 px-3 py-2 text-xs font-medium text-gray-800 bg-gray-50 border border-gray-200 rounded-md shadow-lg pointer-events-none"
            style={{ top: position.top, left: position.left, width: TOOLTIP_WIDTH }}
          >
            {content}
          </div>,
          document.body
        )}
    </span>
  )
}

export default JobInfoTooltip
